import { BRAND } from "@/brand.config";
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtNum } from "@/lib/format";
import {
  Target, Users, MessageSquare, ClipboardCheck, ChevronLeft, ChevronRight,
  Check, Sparkles, Send, Loader2, X, Users2, Play, Save, Database, Ticket, CalendarClock, CheckCircle2,
} from "lucide-react";
import CampaignObjective from "./CampaignObjective";

const CHANNELS = ["whatsapp", "sms", "email", "push", "in_app"];
const STEPS = [
  { key: "objective", label: "Objective", icon: Target },
  { key: "audience", label: "Audience", icon: Users },
  { key: "message", label: "Message", icon: MessageSquare },
  { key: "review", label: "Review & Launch", icon: ClipboardCheck },
];

const BLANK = {
  name: "", description: "", channels: ["sms"], audience_type: "all",
  audience_filter: {}, message_template: "", template_id: "",
  coupon_code: "", coupon_batch_id: "", status: "draft", send_limit: 50000,
  objective: "generic", objective_params: {}, roi_window: "7d", cost: "", link: "",
  tiered: false, points_bands: [],
};

export default function CampaignWizard({ onClose, onDone, templates = [], coupons = [], prefill }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...BLANK, ...(prefill || {}) });
  const [audience, setAudience] = useState(null);      // { total_audience, sample }
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(null);  // 'draft' | 'launch' | 'schedule'
  const [mdbs, setMdbs] = useState([]);
  const [batches, setBatches] = useState([]);
  const [scheduleAt, setScheduleAt] = useState("");
  const [allocating, setAllocating] = useState(false);
  const [nb, setNb] = useState({ coupon_type: "percentage", discount_value: 10, prefix: BRAND.couponPrefix, valid_days: 30 });
  const [msgPreview, setMsgPreview] = useState(null);
  const [previewingMsg, setPreviewingMsg] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => {
    api.get("/marketing-db").then((r) => setMdbs(r.data.rows || [])).catch(() => {});
    api.get("/coupons/unique/batches").then((r) => setBatches(r.data.rows || [])).catch(() => {});
  }, []);

  const toggleChannel = (ch) =>
    setForm((f) => ({ ...f, channels: f.channels.includes(ch) ? f.channels.filter((x) => x !== ch) : [...f.channels, ch] }));

  const compatibleTemplates = useMemo(
    () => templates.filter((t) => form.channels.length === 0 || form.channels.includes(t.channel) || (t.channel === "rcs" && form.channels.includes("whatsapp"))),
    [templates, form.channels]
  );

  const previewAudience = async () => {
    const rid = ++reqRef.current;
    setPreviewing(true);
    try {
      const r = await api.post("/campaigns/audience-preview", {
        audience_type: form.audience_type, audience_filter: form.audience_filter || {},
      });
      if (rid === reqRef.current) setAudience(r.data);  // ignore stale (out-of-order) responses
    } catch (e) {
      if (rid === reqRef.current) { toast.error(e?.response?.data?.detail || "Could not preview audience"); setAudience(null); }
    } finally {
      if (rid === reqRef.current) setPreviewing(false);
    }
  };

  // auto-preview (debounced) whenever we land on the audience/review step or the audience changes
  useEffect(() => {
    if (form.tiered) { setAudience(null); return; }
    if (STEPS[step].key !== "audience" && STEPS[step].key !== "review") return;
    const t = setTimeout(() => { previewAudience(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form.tiered, form.audience_type, JSON.stringify(form.audience_filter)]);

  // Live message preview — resolves the exact text sample recipients get, with LIVE points by
  // mobile. Previews the ACTUAL body that will send (template body if linked, else fallback body).
  const runMessagePreview = async () => {
    const tpl = templates.find((t) => t.id === form.template_id);
    const body = (tpl?.body || form.message_template || "").trim();
    if (!body) { toast.error("Pick a template (or type a preview body) containing {{points}} first"); return; }
    setPreviewingMsg(true);
    try {
      const r = await api.post("/campaigns/message-preview", {
        body, audience_type: form.audience_type, audience_filter: form.audience_filter || {}, link: form.link || null,
      });
      setMsgPreview(r.data);
      if (!(r.data.samples || []).length) toast.info("No sample recipients found for this audience yet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewingMsg(false); }
  };

  const stepValid = () => {
    const k = STEPS[step].key;
    if (k === "objective") return !!form.name.trim() && (!form.tiered || (form.points_bands || []).length > 0);
    if (k === "audience") {
      if (form.tiered) return true;
      if (form.audience_type === "segment") return !!form.audience_filter?.segment_id;
      if (form.audience_type === "marketing_db") return !!form.audience_filter?.marketing_db_id;
      if (form.audience_type === "tier") return !!form.audience_filter?.tier;
      if (form.audience_type === "city") return !!form.audience_filter?.city;
      if (form.audience_type === "points") return (form.audience_filter?.min_points || 0) > 0;
      if (form.audience_type === "expiring_points") return true;
      if (form.audience_type === "cohort") return !!form.audience_filter?.cohort;
      return true;
    }
    if (k === "message") { if (form.tiered) return true; return form.channels.length > 0; }
    return true;
  };

  // Generate a unique per-recipient coupon batch for THIS campaign's audience (KAZO Point 3 —
  // coupon campaigns were undoable because no batch could be created). Needs a segment or
  // marketing-DB audience (those resolve to a mobile list).
  const generateBatch = async () => {
    const src = form.audience_type === "segment" && form.audience_filter?.segment_id
      ? { source: "segment", segment_id: form.audience_filter.segment_id }
      : form.audience_type === "marketing_db" && form.audience_filter?.marketing_db_id
        ? { source: "marketing_db", marketing_db_id: form.audience_filter.marketing_db_id }
        : null;
    if (!src) { toast.error("Unique codes need a Segment or Marketing-DB audience"); return; }
    setAllocating(true);
    try {
      const now = new Date();
      const to = new Date(now.getTime() + (parseInt(nb.valid_days, 10) || 30) * 86400000);
      const r = await api.post("/coupons/unique/allocate", {
        name: `${form.name || "Campaign"} codes`,
        coupon_type: nb.coupon_type,
        discount_value: parseFloat(nb.discount_value) || 0,
        valid_from: now.toISOString(), valid_to: to.toISOString(),
        prefix: nb.prefix || BRAND.couponPrefix, audience: src,
      });
      toast.success(`Generated ${fmtNum(r.data.total)} unique codes`);
      const b = await api.get("/coupons/unique/batches");
      setBatches(b.data.rows || []);
      setForm((f) => ({ ...f, coupon_batch_id: r.data.id }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate codes");
    } finally { setAllocating(false); }
  };

  const buildPayload = (mode) => {
    const payload = { ...form };
    if (!payload.template_id) delete payload.template_id;
    if (!payload.coupon_batch_id) delete payload.coupon_batch_id;
    if (!payload.message_template && !payload.template_id) payload.message_template = "(no body)";
    if (payload.cost === "" || payload.cost == null) delete payload.cost;
    else payload.cost = parseFloat(payload.cost);
    if (mode === "schedule") {
      payload.status = "scheduled";
      payload.schedule_at = new Date(scheduleAt).toISOString();
    }
    return payload;
  };

  const submit = async (mode) => {
    if (mode === "schedule") {
      if (!scheduleAt) { toast.error("Pick a date & time to schedule"); return; }
      if (new Date(scheduleAt).getTime() <= Date.now()) { toast.error("Schedule time must be in the future"); return; }
    }
    setSubmitting(mode);
    try {
      const r = await api.post("/campaigns", buildPayload(mode));
      const created = r.data;
      if (mode === "launch") {
        const lr = await api.post(`/campaigns/${created.id}/launch`);
        if (lr.data.mode === "karix") toast.success(`Real send via ${lr.data.channel?.toUpperCase()} queued · audience ${fmtNum(lr.data.audience)}`);
        else toast.success(`Simulated metrics generated · audience ${fmtNum(lr.data.audience)} (link a template for real send)`);
      } else if (mode === "schedule") {
        toast.success(`Campaign scheduled for ${new Date(scheduleAt).toLocaleString()} — it will fire automatically`);
      } else {
        toast.success("Campaign saved as draft");
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSubmitting(null); }
  };

  // Tiered points campaign — one campaign per band (each with its own points range + template).
  const submitTiered = async (mode) => {
    const bands = form.points_bands || [];
    if (!bands.length) { toast.error("Add at least one points band in the Objective step"); return; }
    if (mode === "schedule") {
      if (!scheduleAt) { toast.error("Pick a date & time to schedule"); return; }
      if (new Date(scheduleAt).getTime() <= Date.now()) { toast.error("Schedule time must be in the future"); return; }
    }
    setSubmitting(mode);
    try {
      const r = await api.post("/campaigns/tiered-points", {
        name: form.name, description: form.description, channels: form.channels,
        roi_window: form.roi_window,
        cost: (form.cost === "" || form.cost == null) ? null : parseFloat(form.cost),
        link: form.link || null, coupon_code: form.coupon_code, coupon_batch_id: form.coupon_batch_id || "",
        send_limit: form.send_limit,
        bands: bands.map((b) => ({
          label: b.label || null,
          min_points: parseInt(b.min_points, 10) || 1,
          max_points: (b.max_points === "" || b.max_points == null) ? null : parseInt(b.max_points, 10),
          template_id: b.template_id || null,
          message_template: form.message_template || null,
        })),
        mode, schedule_at: mode === "schedule" ? new Date(scheduleAt).toISOString() : null,
      });
      const list = r.data.campaigns || [];
      const errs = list.filter((c) => c.error);
      const verb = mode === "launch" ? "launched" : mode === "schedule" ? "scheduled" : "saved";
      if (errs.length) toast.warning(`${list.length - errs.length}/${list.length} band campaigns ${verb} · ${errs.length} had errors (${errs[0].error})`);
      else toast.success(`${list.length} band campaign${list.length === 1 ? "" : "s"} ${verb}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tiered campaign");
    } finally { setSubmitting(null); }
  };


  const next = () => { if (!stepValid()) { toast.error("Please complete this step"); return; } setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const key = STEPS[step].key;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[92vh] rounded flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="campaign-wizard">
        {/* header + stepper */}
        <div className="p-5 border-b border-black/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl">New Campaign</h3>
            <button onClick={onClose} className="k-btn k-btn-ghost" data-testid="wizard-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => {
              const Icon = s.icon; const done = i < step; const active = i === step;
              return (
                <div key={s.key} className="flex items-center flex-1 last:flex-none">
                  <button type="button" onClick={() => i < step && setStep(i)} data-testid={`wizard-step-${s.key}`}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded transition ${active ? "kazo-bg-burgundy text-white" : done ? "text-emerald-700" : "text-neutral-400"}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${active ? "bg-white/20" : done ? "bg-emerald-100" : "bg-neutral-100"}`}>
                      {done ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                    </span>
                    <span className="text-xs font-medium hidden sm:block">{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-1 ${done ? "bg-emerald-300" : "bg-neutral-200"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {key === "objective" && (
            <div className="space-y-3" data-testid="wizard-body-objective">
              <input className="k-input" placeholder="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
              <textarea className="k-input" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <CampaignObjective form={form} setForm={setForm} templates={templates} />
            </div>
          )}

          {key === "audience" && (
            <div className="space-y-3" data-testid="wizard-body-audience">
              {form.tiered && (
                <div className="border border-indigo-200 rounded p-4 bg-indigo-50/50 text-sm text-neutral-700" data-testid="audience-tiered-note">
                  <b>Tiered mode is on.</b> Audiences are defined per points band in the Objective step ({(form.points_bands || []).length} band{(form.points_bands || []).length === 1 ? "" : "s"}). Each band sends to its own points range — continue to <b>Message</b> → <b>Review</b> to launch them all.
                </div>
              )}
              {!form.tiered && (<>
              <div className="grid grid-cols-2 gap-3">
                <select className="k-input" value={form.audience_type} onChange={(e) => setForm({ ...form, audience_type: e.target.value, audience_filter: {} })} data-testid="audience-type-select">
                  <option value="all">Entire base</option>
                  <option value="segment">From Segment Builder</option>
                  <option value="marketing_db">From Marketing Database</option>
                  <option value="tier">By tier</option>
                  <option value="city">By city</option>
                  <option value="points">By points balance</option>
                  <option value="expiring_points">Points expiring soon</option>
                  <option value="cohort">By cohort</option>
                </select>
                {form.audience_type === "segment" && (
                  <div className="k-input flex items-center gap-2 bg-amber-50 border-amber-300">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                    <span className="text-xs font-medium">{form.audience_filter?.segment_name || form.audience_filter?.segment_id || "No segment selected"}</span>
                  </div>
                )}
                {form.audience_type === "marketing_db" && (
                  <select className="k-input" value={form.audience_filter?.marketing_db_id || ""}
                    onChange={(e) => { const m = mdbs.find((x) => x.id === e.target.value); setForm({ ...form, audience_filter: { marketing_db_id: e.target.value, marketing_db_name: m?.name } }); }}
                    data-testid="audience-mdb-select">
                    <option value="">Select a database</option>
                    {mdbs.map((m) => <option key={m.id} value={m.id}>{`${m.name} · ${m.total} numbers (${m.member_count} members)`}</option>)}
                  </select>
                )}
                {form.audience_type === "tier" && (
                  <select className="k-input" value={form.audience_filter?.tier || ""} onChange={(e) => setForm({ ...form, audience_filter: { tier: e.target.value } })} data-testid="audience-tier-select">
                    <option value="">Select tier</option>
                    <option value="silver">Silver</option><option value="gold">Gold</option><option value="platinum">Platinum</option><option value="diamond">Diamond</option>
                  </select>
                )}
                {form.audience_type === "city" && (
                  <input className="k-input" placeholder="City" value={form.audience_filter?.city || ""} onChange={(e) => setForm({ ...form, audience_filter: { city: e.target.value } })} data-testid="audience-city-input" />
                )}
                {form.audience_type === "points" && (
                  <input className="k-input" type="number" min="1" placeholder="Min points held (e.g. 100)"
                    value={form.audience_filter?.min_points || ""}
                    onChange={(e) => setForm({ ...form, audience_filter: { min_points: parseInt(e.target.value, 10) || 0 } })}
                    data-testid="audience-minpoints-input" />
                )}
                {form.audience_type === "expiring_points" && (
                  <select className="k-input" value={form.audience_filter?.expiry_days || 30}
                    onChange={(e) => setForm({ ...form, audience_filter: { expiry_days: parseInt(e.target.value, 10) } })}
                    data-testid="audience-expiry-days">
                    <option value={7}>Expiring within 7 days</option>
                    <option value={15}>Expiring within 15 days</option>
                    <option value={30}>Expiring within 30 days</option>
                    <option value={60}>Expiring within 60 days</option>
                    <option value={90}>Expiring within 90 days</option>
                  </select>
                )}
                {form.audience_type === "cohort" && (
                  <select className="k-input" value={form.audience_filter?.cohort || ""} onChange={(e) => setForm({ ...form, audience_filter: { cohort: e.target.value } })} data-testid="audience-cohort-select">
                    <option value="">Select cohort</option>
                    <option value="high_value">High-value</option><option value="churn_risk">Churn risk</option><option value="new">New customers</option><option value="vip">VIP (Platinum/Diamond)</option>
                  </select>
                )}
              </div>

              <div className="border border-black/10 rounded p-4 bg-neutral-50" data-testid="audience-preview-box">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-neutral-600"><Users2 className="w-4 h-4" /> Estimated dataset</div>
                  <button type="button" className="k-btn k-btn-sm k-btn-ghost" onClick={previewAudience} disabled={previewing} data-testid="audience-refresh">
                    {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
                  </button>
                </div>
                {previewing && !audience ? (
                  <div className="text-neutral-400 text-sm py-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Counting…</div>
                ) : audience ? (
                  <>
                    <div className="text-3xl font-display mt-2" data-testid="audience-count">{fmtNum(audience.total_audience)}</div>
                    <div className="text-[11px] text-neutral-500">customers match · capped at your send limit · blacklisted numbers auto-excluded ({fmtNum(audience.blacklisted_total || 0)} on blacklist)</div>
                    {audience.has_coupon_column && (
                      <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="coupon-column-hint">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Coupon column detected — <code className="font-mono">{"{{coupon_code}}"}</code> will be personalised per customer from this database.
                      </div>
                    )}
                    {audience.sample?.length > 0 && (
                      <div className="flex gap-1.5 flex-wrap mt-2">
                        {audience.sample.slice(0, 8).map((s, i) => (
                          <span key={i} className="pill pill-neutral text-[10px]">{s.name || s.mobile} · {s.tier || "—"}</span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-neutral-400 text-sm py-3">No preview yet</div>
                )}
              </div>
              </>)}
            </div>
          )}

          {key === "message" && (
            <div className="space-y-3" data-testid="wizard-body-message">
              {form.tiered && (
                <div className="border border-indigo-200 rounded p-3 bg-indigo-50/50 text-sm text-neutral-700" data-testid="message-tiered-note">
                  <b>Tiered mode:</b> each points band uses its own template (set in the Objective step). The channel, link, coupon and cost below apply to every band.
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 block">Channels</label>
                <div className="flex gap-2 flex-wrap">
                  {CHANNELS.map((ch) => (
                    <button key={ch} type="button" onClick={() => toggleChannel(ch)} className={`pill ${form.channels.includes(ch) ? "kazo-bg-burgundy text-white" : "pill-neutral"}`} data-testid={`channel-${ch}`}>{ch}</button>
                  ))}
                </div>
              </div>

              {!form.tiered && (
              <div className="border border-neutral-200 rounded p-3 bg-neutral-50" data-testid="campaign-template-select">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase tracking-widest text-neutral-700 font-medium flex items-center gap-1"><Send className="w-3 h-3" /> Send via Telecom Provider template (real send)</label>
                  {form.template_id && <button type="button" onClick={() => setForm({ ...form, template_id: "" })} className="text-[10px] text-rose-600 hover:underline">Clear</button>}
                </div>
                {compatibleTemplates.length === 0 ? (
                  <div className="text-xs text-neutral-500" data-testid="campaign-template-empty">
                    No active templates for selected channels — create one in <a href="/admin/communications/templates" className="underline text-burgundy">Templates</a>. Without a template the campaign only generates simulated metrics.
                  </div>
                ) : (
                  <select className="k-input" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} data-testid="campaign-template-dropdown">
                    <option value="">— Simulated metrics only (no real send) —</option>
                    {compatibleTemplates.map((t) => (
                      <option key={t.id} value={t.id}>{`${t.name} · ${t.channel.toUpperCase()}${t.channel !== "sms" ? ` · WABA ${t.waba_approval_status || "pending"}` : ""}`}</option>
                    ))}
                  </select>
                )}
                {form.template_id && <div className="text-[10px] text-emerald-700 mt-1">✓ This campaign will actually send messages via Telecom Provider when launched.</div>}
                {(() => {
                  const sel = templates.find((t) => t.id === form.template_id);
                  if (sel && sel.channel === "sms" && !(sel.dlt_template_id || "").trim()) {
                    return <div className="text-[10px] text-rose-600 mt-1" data-testid="tpl-no-dlt-warning">⚠ This SMS template has no DLT content template ID — launch will be blocked. Add it in Templates first, and make sure the body matches your DLT-approved content verbatim.</div>;
                  }
                  return null;
                })()}
              </div>
              )}

              <textarea className="k-input" placeholder="Optional preview / fallback body (only used if no Telecom Provider template above)" value={form.message_template} onChange={(e) => setForm({ ...form, message_template: e.target.value })} data-testid="message-template-input" />

              <div className="border border-indigo-200 rounded p-3 bg-indigo-50/60">
                <label className="text-[10px] uppercase tracking-widest text-indigo-700 font-medium flex items-center gap-1 mb-2"><Sparkles className="w-3 h-3" /> Personalisation link — fills <code className="bg-white px-1 rounded border font-mono">{"{{link}}"}</code></label>
                <input className="k-input" type="url" placeholder={`https://stores.${BRAND.domain}/`} value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="campaign-link-input" />
                <div className="text-[10px] text-neutral-600 mt-2 leading-relaxed" data-testid="campaign-variables-hint">
                  Variables you can put in the template body:
                  {" "}<code className="bg-white px-1 rounded border">{"{{name}}"}</code>{" "}
                  <code className="bg-white px-1 rounded border">{"{{points}}"}</code>{" "}
                  <code className="bg-white px-1 rounded border">{"{{expiry}}"}</code>{" "}
                  <code className="bg-white px-1 rounded border">{"{{link}}"}</code>{" "}
                  <code className="bg-white px-1 rounded border">{"{{coupon_code}}"}</code>.
                  <br />Loyalty/segment audiences fill these from the customer profile ({"{{expiry}}"} = their own points-expiry date). Uploaded Marketing DBs use your file's <b>name / points / expiry / link</b> columns.
                </div>
              </div>

              {!form.tiered && (
              <div className="border border-emerald-200 rounded p-3 bg-emerald-50/50" data-testid="msg-preview-box">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase tracking-widest text-emerald-800 font-medium flex items-center gap-1"><Sparkles className="w-3 h-3" /> Live preview — resolves real points by mobile</label>
                  <button type="button" className="k-btn k-btn-sm k-btn-ghost" onClick={runMessagePreview} disabled={previewingMsg} data-testid="msg-preview-btn">
                    {previewingMsg ? <Loader2 className="w-3 h-3 animate-spin" /> : "Preview"}
                  </button>
                </div>
                {msgPreview?.samples?.length > 0 ? (
                  <div className="space-y-1.5" data-testid="msg-preview-list">
                    {msgPreview.samples.map((s, i) => (
                      <div key={i} className="text-xs bg-white border rounded p-2" data-testid={`msg-preview-${i}`}>
                        <div className="text-[10px] text-neutral-400 mb-0.5">{s.mobile} · {s.is_member ? `${fmtNum(s.points)} pts` : "non-member · 0 pts"}</div>
                        <div className="text-neutral-800">{s.rendered}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[11px] text-neutral-500">Click Preview to see the exact message a few sample recipients will receive, with their live points balance filled in.</div>
                )}
              </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <select className="k-input" value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value })} data-testid="coupon-select">
                  <option value="">No coupon</option>
                  {coupons.map((c) => <option key={c.id} value={c.code}>{`${c.code} · ${c.name}`}</option>)}
                </select>
                <input className="k-input" type="number" min="1" max="500000" placeholder="Send limit" value={form.send_limit} onChange={(e) => setForm({ ...form, send_limit: parseInt(e.target.value, 10) || 50000 })} data-testid="send-limit-input" />
                <input className="k-input" type="number" min="0" placeholder="Spend / cost (₹)" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="campaign-cost-input" />
              </div>

              <div className="border border-neutral-200 rounded p-3 bg-neutral-50">
                <label className="text-[10px] uppercase tracking-widest text-neutral-700 font-medium flex items-center gap-1 mb-2"><Ticket className="w-3 h-3" /> Attach unique coupon batch (per-recipient code)</label>
                <select className="k-input" value={form.coupon_batch_id} onChange={(e) => setForm({ ...form, coupon_batch_id: e.target.value })} data-testid="coupon-batch-select">
                  <option value="">None</option>
                  {batches.map((b) => <option key={b.id} value={b.id}>{`${b.name} · ${b.total} codes (${b.total - (b.redeemed || 0)} left)`}</option>)}
                </select>
                {form.coupon_batch_id && <div className="text-[10px] text-emerald-700 mt-1">✓ Each recipient gets THEIR own code — include <code className="bg-neutral-100 px-1">{"{{coupon_code}}"}</code> in the template body.</div>}

                {/* Generate a brand-new unique batch for this audience (Segment / Marketing DB) */}
                <div className="mt-3 pt-3 border-t border-neutral-200" data-testid="coupon-batch-generate">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">…or generate a new batch for this audience</div>
                  {(form.audience_type === "segment" && form.audience_filter?.segment_id) ||
                   (form.audience_type === "marketing_db" && form.audience_filter?.marketing_db_id) ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <select className="k-input" value={nb.coupon_type} onChange={(e) => setNb({ ...nb, coupon_type: e.target.value })} data-testid="nb-type">
                          <option value="percentage">% off</option>
                          <option value="flat">₹ flat</option>
                        </select>
                        <input className="k-input" type="number" min="1" placeholder="Value" value={nb.discount_value} onChange={(e) => setNb({ ...nb, discount_value: e.target.value })} data-testid="nb-value" />
                        <input className="k-input" placeholder="Prefix" value={nb.prefix} onChange={(e) => setNb({ ...nb, prefix: e.target.value })} data-testid="nb-prefix" />
                        <input className="k-input" type="number" min="1" placeholder="Valid days" value={nb.valid_days} onChange={(e) => setNb({ ...nb, valid_days: e.target.value })} data-testid="nb-days" />
                      </div>
                      <button type="button" onClick={generateBatch} disabled={allocating} className="k-btn k-btn-sm kazo-bg-burgundy mt-2 disabled:opacity-50" data-testid="nb-generate">
                        {allocating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ticket className="w-3 h-3" />} {allocating ? "Generating…" : "Generate unique codes"}
                      </button>
                    </>
                  ) : (
                    <div className="text-[10px] text-neutral-500" data-testid="nb-need-audience">Pick a <b>Segment</b> or <b>Marketing Database</b> audience to generate per-recipient unique codes.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {key === "review" && (
            <div className="space-y-4" data-testid="wizard-body-review">
              <div className="grid grid-cols-2 gap-3">
                <ReviewItem label="Name" value={form.name} testid="review-name" />
                <ReviewItem label="Objective" value={`${form.objective} · ROI ${form.roi_window}`} testid="review-objective" />
                <ReviewItem label="Audience" value={audience ? `${fmtNum(audience.total_audience)} customers · ${form.audience_type}` : form.audience_type} testid="review-audience" />
                <ReviewItem label="Channels" value={form.channels.join(", ") || "—"} testid="review-channels" />
                <ReviewItem label="Send mode" value={form.template_id ? "Real Telecom Provider send" : "Simulated metrics"} testid="review-sendmode" accent={form.template_id ? "text-emerald-700" : "text-neutral-500"} />
                <ReviewItem label="Send limit" value={fmtNum(form.send_limit)} testid="review-limit" />
                {form.coupon_code && <ReviewItem label="Coupon" value={form.coupon_code} testid="review-coupon" />}
                {form.coupon_batch_id && <ReviewItem label="Unique coupons" value="Per-recipient codes attached" testid="review-coupon-batch" accent="text-emerald-700" />}
                {form.cost !== "" && form.cost != null && <ReviewItem label="Spend" value={`₹${form.cost}`} testid="review-cost" />}
              </div>

              {form.tiered && (
                <div className="border border-indigo-200 rounded p-3 bg-indigo-50/40" data-testid="review-bands">
                  <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-medium mb-2">Tiered points bands — {(form.points_bands || []).length} separate campaign{(form.points_bands || []).length === 1 ? "" : "s"}</div>
                  <div className="space-y-1.5">
                    {(form.points_bands || []).map((b, i) => {
                      const tpl = templates.find((t) => t.id === b.template_id);
                      const label = (b.label || "").trim() || (b.max_points === "" || b.max_points == null ? `${b.min_points || 1}+` : `${b.min_points}-${b.max_points}`);
                      return (
                        <div key={i} className="flex items-center justify-between text-xs bg-white border rounded px-2.5 py-1.5" data-testid={`review-band-${i}`}>
                          <span className="font-medium">{label} pts</span>
                          <span className="text-neutral-500">{tpl ? tpl.name : "Simulated (no template)"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border border-black/10 rounded p-3 bg-white" data-testid="review-schedule">
                <label className="text-[10px] uppercase tracking-widest text-neutral-700 font-medium flex items-center gap-1 mb-2"><CalendarClock className="w-3 h-3" /> Schedule for later (optional)</label>
                <input type="datetime-local" className="k-input" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="schedule-at-input" />
                {scheduleAt && <div className="text-[11px] text-emerald-700 mt-1">Will auto-fire at {new Date(scheduleAt).toLocaleString()} — no manual action needed.</div>}
              </div>

              <div className="text-[11px] text-neutral-500 bg-amber-50 border border-amber-200 rounded p-2.5">
                Launching freezes this exact audience as the ROI dataset (t₀ = launch). Real Telecom Provider sends go via the bulk campaign account; simulated campaigns generate demo metrics.
              </div>
            </div>
          )}
        </div>

        {/* footer */}
        <div className="p-4 border-t border-black/10 flex items-center justify-between">
          <button className="k-btn k-btn-ghost disabled:opacity-40" onClick={back} disabled={step === 0} data-testid="wizard-back"><ChevronLeft className="w-4 h-4" /> Back</button>
          {key !== "review" ? (
            <button className="k-btn kazo-bg-burgundy" onClick={next} data-testid="wizard-next">Next <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <div className="flex gap-2">
              <button className="k-btn k-btn-ghost disabled:opacity-50" onClick={() => (form.tiered ? submitTiered("draft") : submit("draft"))} disabled={!!submitting} data-testid="wizard-create-draft">
                {submitting === "draft" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save draft
              </button>
              {scheduleAt ? (
                <button className="k-btn kazo-bg-burgundy disabled:opacity-50" onClick={() => (form.tiered ? submitTiered("schedule") : submit("schedule"))} disabled={!!submitting} data-testid="wizard-create-schedule">
                  {submitting === "schedule" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Schedule
                </button>
              ) : (
                <button className="k-btn kazo-bg-burgundy disabled:opacity-50" onClick={() => (form.tiered ? submitTiered("launch") : submit("launch"))} disabled={!!submitting} data-testid="wizard-create-launch">
                  {submitting === "launch" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {form.tiered ? "Create & Launch all" : "Create & Launch"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewItem({ label, value, testid, accent }) {
  return (
    <div className="border border-black/10 rounded p-3 bg-neutral-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm mt-0.5 font-medium ${accent || ""}`}>{value || "—"}</div>
    </div>
  );
}
